/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from "react";
import { MEASURES, type MeasureDef, type MeasureId, isDiscreteMeasure } from "../config/metrics";
import { FORCED_MEASURE_GRANULARITY, type PlotChartType, type TimeGrain } from "../config/plots";
import type { Timeseries } from "../types";
import { type DBPool } from "../duckdb";
import { useAuth } from "../auth/AuthContext";
import { useDatabase } from "../database/DatabaseContext";
import { runBigQueryQuery } from "../bigquery/BigQueryClient";
import { resampleTimeseries } from "../data/resample";
import SearchableSelect from "./SearchableSelect";
import { MetricChart } from "./MetricChartsSection";
import { buildFlightsSQL, type Flight } from "./experimentFlights";

/**
 * Experiment custom charts — the experiment-page counterpart to the dashboard's
 * `CustomPlots`. Pick a metric + chart type and get one chart per prediction flight,
 * rendered side by side. Data is read from the same WPR-metrics (BigQuery-backed)
 * source as the main dashboard, sliced on the `primary_prediction_flight_id`
 * dimension and scoped to the experiment's own window — independent of the global
 * dashboard time filter.
 *
 * For now this mirrors the dashboard's per-day/week time-series charts so we can reuse
 * `MetricChart` directly. A later iteration will likely add aggregate-over-the-period
 * charts (a single value per flight) and the dimension-aggregating chart types
 * (boxplot / distribution) that are skipped here.
 */

const FLIGHT_DIMENSION = "primary_prediction_flight_id";

// A metric can be sliced by flight when it has no dimension restriction (all dims
// allowed) or its allow-list explicitly includes the flight dimension. This mirrors
// the `filterDimensions` gate used by the dashboard's Plots.
function supportsFlight(measure: MeasureDef): boolean {
  return !measure.filterDimensions || measure.filterDimensions.includes(FLIGHT_DIMENSION);
}

const MEASURE_OPTIONS = MEASURES.filter(supportsFlight)
  .map(m => ({ value: m.id, label: m.label }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Per-flight charts are single-series time series, so only the time-series chart types
// apply (boxplot / distribution aggregate across a dimension and are skipped for now).
const DISCRETE_CHART_OPTIONS: { value: PlotChartType; label: string }[] = [
  { value: "xmr", label: "XmR" },
  { value: "xmr-log", label: "XmR (log scale)" },
  { value: "ewma", label: "EWMA" },
  { value: "line", label: "Line" },
];
const CONTINUOUS_CHART_OPTIONS: { value: PlotChartType; label: string }[] = [
  { value: "x", label: "X (individuals)" },
  { value: "line", label: "Line" },
];

interface CustomPlotEntry {
  id: string;
  measureId: MeasureId;
  chartType: PlotChartType;
}

/** flightId -> daily series for the chosen metric */
type FlightSeries = Record<string, Timeseries>;

interface Props {
  experimentId: string;
  startTime: string | null;
  endTime: string | null;
}

/** Flight names carry a redundant prefix on the experiment page — strip it for the chart title. */
function cleanName(name: string, flightId: string): string {
  const cleaned = (name || "")
    .replace(/^\(copy of\)\s*/i, "")
    .replace(/\[[^\]]*\]\s*/g, "")
    .replace(/[:\-–]\s*\d+\s*$/, "")
    .trim();
  return cleaned || `Flight ${flightId}`;
}

function toDateStr(d: Date): string {
  return d.toISOString().split("T")[0];
}

function sqlString(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

// One PIVOT query → a column per flight, scoped to this experiment's flights and window.
// Mirrors DataProvider's dimension-series query, restricted to the flight dimension.
async function queryFlightSeries(
  dbPool: DBPool,
  valueExpr: string,
  flightIds: string[],
  startDate: string,
  endDate: string,
): Promise<FlightSeries> {
  const inList = flightIds.map(sqlString).join(",");
  const sql = `PIVOT (
    SELECT bid_date AS ts, dim_val1 AS dim, ${valueExpr} AS val
    FROM aggregated_data
    WHERE group_type='${FLIGHT_DIMENSION}'
      AND dim_val1 IN (${inList})
      AND bid_date >= DATE '${startDate}' AND bid_date <= DATE '${endDate}'
  ) ON dim USING MAX(val) ORDER BY ts;`;

  const db = dbPool.getDB();
  const conn = await db.connect();
  try {
    const result = await conn.query(sql);
    const rows = result.toArray().map((r: any) => r.toJSON());
    if (rows.length === 0) return {};
    const cols = Object.keys(rows[0]).filter(c => c !== "ts");
    const series: FlightSeries = {};
    for (const col of cols) {
      series[col] = {
        timestamps: rows.map((r: any) => new Date(r.ts)),
        data: rows.map((r: any) => (r[col] != null ? Number(r[col]) : null)) as any,
      };
    }
    return series;
  } finally {
    await conn.close();
  }
}

// Per-flight cell shown while a metric is loading or when a flight has no data in the
// window — keeps the side-by-side grid intact (MetricChart renders nothing on empty data).
function Placeholder({ title, message }: { title: string; message: string }) {
  return (
    <div className="experiment-custom-plots__placeholder">
      <span className="experiment-custom-plots__placeholder-title">{title}</span>
      <span>{message}</span>
    </div>
  );
}

export default function ExperimentCustomPlots({ experimentId, startTime, endTime }: Props) {
  const { isAuthenticated, requiresAuth } = useAuth();
  const { dbPool } = useDatabase();

  const [flights, setFlights] = useState<Flight[]>([]);
  const [entries, setEntries] = useState<CustomPlotEntry[]>([]);
  const [seriesByMeasure, setSeriesByMeasure] = useState<Record<string, FlightSeries>>({});
  const [errorByMeasure, setErrorByMeasure] = useState<Record<string, string>>({});
  const [pendingMeasure, setPendingMeasure] = useState<MeasureId | "">("");
  const [pendingChart, setPendingChart] = useState<PlotChartType | "">("");
  const [error, setError] = useState<string | null>(null);

  // Experiment window: start → min(end, now). Charts are always scoped to this,
  // regardless of the dashboard's global time filter.
  const range = useMemo(() => {
    const start = startTime ? new Date(startTime) : null;
    const rawEnd = endTime ? new Date(endTime) : null;
    const now = new Date();
    const end = rawEnd && rawEnd < now ? rawEnd : now;
    if (!start || Number.isNaN(start.getTime())) return null;
    return { start: toDateStr(start), end: toDateStr(end) };
  }, [startTime, endTime]);

  const grain: TimeGrain = useMemo(() => {
    if (!range) return "day";
    const diffDays = (new Date(range.end).getTime() - new Date(range.start).getTime()) / (1000 * 60 * 60 * 24);
    return diffDays > 63 ? "week" : "day";
  }, [range]);

  // Control arm first, matching the report's arm ordering.
  const orderedFlights = useMemo(
    () => [...flights].sort((a, b) => Number(b.is_control) - Number(a.is_control)),
    [flights],
  );

  // Load the experiment's flights (BigQuery) — gives the flight ids to slice on plus
  // names and the control marker for chart titles/ordering.
  useEffect(() => {
    if (requiresAuth && !isAuthenticated) return;
    let cancelled = false;
    runBigQueryQuery(buildFlightsSQL(experimentId))
      .then(res => {
        if (cancelled) return;
        const fl = (res as any[]).map(f => ({
          flight_id: String(f.flight_id),
          name: f.name,
          is_control: Boolean(f.is_control),
        }));
        setFlights(fl);
      })
      .catch(err => !cancelled && setError(String(err)));
    return () => {
      cancelled = true;
    };
  }, [experimentId, isAuthenticated, requiresAuth]);

  const selectedMeasure = useMemo(() => MEASURES.find(m => m.id === pendingMeasure), [pendingMeasure]);
  const chartOptions = selectedMeasure
    ? isDiscreteMeasure(selectedMeasure)
      ? DISCRETE_CHART_OPTIONS
      : CONTINUOUS_CHART_OPTIONS
    : [];

  const canAdd = !!pendingMeasure && !!pendingChart && orderedFlights.length > 0;

  // Fetch per-flight series for any metric used by an entry that hasn't been loaded yet.
  useEffect(() => {
    if (!dbPool || !range || orderedFlights.length === 0) return;
    const flightIds = orderedFlights.map(f => f.flight_id);
    const needed = Array.from(new Set(entries.map(e => e.measureId))).filter(
      id => !(id in seriesByMeasure) && !(id in errorByMeasure),
    );
    if (needed.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const measureId of needed) {
        const measure = MEASURES.find(m => m.id === measureId);
        if (!measure) continue;
        const valueExpr = isDiscreteMeasure(measure) ? measure.sql : (measure.meanExpr ?? `${measure.sql}_mean`);
        try {
          const series = await queryFlightSeries(dbPool, valueExpr, flightIds, range.start, range.end);
          if (cancelled) return;
          setSeriesByMeasure(prev => ({ ...prev, [measureId]: series }));
        } catch (err) {
          console.warn(`[ExperimentCustomPlots] query failed for ${measureId}:`, err);
          if (!cancelled) setErrorByMeasure(prev => ({ ...prev, [measureId]: String(err) }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dbPool, range, orderedFlights, entries, seriesByMeasure, errorByMeasure]);

  const handleAdd = () => {
    if (!canAdd) return;
    setEntries(prev => [
      ...prev,
      {
        id: `exp-custom-${pendingMeasure}-${pendingChart}-${Date.now()}`,
        measureId: pendingMeasure as MeasureId,
        chartType: pendingChart as PlotChartType,
      },
    ]);
    setPendingMeasure("");
    setPendingChart("");
  };

  return (
    <div className="experiment-custom-plots">
      {error && <p className="entity-list__error-detail">{error}</p>}
      <div className="custom-plots-toolbar">
        <SearchableSelect
          value={pendingMeasure}
          options={MEASURE_OPTIONS}
          onChange={id => {
            setPendingMeasure(id as MeasureId);
            setPendingChart("");
          }}
          placeholder="Select metric…"
          className="custom-plots-toolbar__metric-select"
          dropUp
        />
        <SearchableSelect
          value={pendingChart}
          options={chartOptions}
          onChange={t => setPendingChart(t as PlotChartType)}
          placeholder="Chart type…"
          className="custom-plots-toolbar__chart-select"
          dropUp
        />
        <button type="button" className="custom-plots-toolbar__add-btn" onClick={handleAdd} disabled={!canAdd}>
          Add chart
        </button>
      </div>

      {entries.length === 0 && (
        <p className="experiment-custom-plots__hint">
          Add a chart to compare a metric across this experiment&rsquo;s prediction flights, side by side.
        </p>
      )}

      {entries.map(entry => {
        const measure = MEASURES.find(m => m.id === entry.measureId)!;
        const series = seriesByMeasure[entry.measureId];
        const measureError = errorByMeasure[entry.measureId];
        const loaded = entry.measureId in seriesByMeasure;
        const forcedDaily = FORCED_MEASURE_GRANULARITY[entry.measureId] === "day";
        const strategy: "sum" | "average" = measure.isCumulative && !forcedDaily ? "sum" : "average";
        return (
          <div key={entry.id} className="experiment-custom-plots__item">
            <div className="custom-plot-item__bar">
              <span className="experiment-custom-plots__title">{measure.label}</span>
              <button
                type="button"
                className="custom-plot-item__remove"
                onClick={() => setEntries(prev => prev.filter(e => e.id !== entry.id))}
              >
                Remove
              </button>
            </div>
            {measureError ? (
              <p className="entity-list__error-detail">Could not load this metric: {measureError}</p>
            ) : (
              <div className="experiment-custom-plots__row">
                {orderedFlights.map(flight => {
                  const title = cleanName(flight.name, flight.flight_id) + (flight.is_control ? " · control" : "");
                  const raw = series?.[flight.flight_id];
                  const hasData = !!raw && raw.data.some(v => v != null);
                  const data =
                    raw && grain === "week" && raw.timestamps.length > 0
                      ? resampleTimeseries(raw, "week", strategy)
                      : raw;
                  return (
                    <div key={flight.flight_id} className="experiment-custom-plots__chart">
                      {!loaded ? (
                        <Placeholder title={title} message="Loading…" />
                      ) : hasData && data ? (
                        <MetricChart
                          title={title}
                          chart={entry.chartType}
                          data={data}
                          measure={measure}
                          grain={grain}
                        />
                      ) : (
                        <Placeholder title={title} message="No data for this flight in the experiment window." />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
